import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, TextField, Snackbar, Alert, Typography,
} from '@mui/material';
import {
  buscarInfoInicialLimparEnvioEmail,
  submitFormEnvioEmailDevedor,
} from '../../../../services/core/processos/processosEnviarEmailDevedorService';

const MENSAGEM_ERRO_GENERICA = 'Erro no envio do e-mail. Tente novamente.';
const MENSAGEM_ERRO_INICIAL = 'Erro ao buscar informações de email.';
const MENSAGEM_REVISAR = 'Revise as informações e preencha o cadastro corretamente';
// 'emailDe' fica de fora: o campo é somente leitura, então um erro de
// validação nele não é acionável pelo usuário — cai no Snackbar genérico.
const CAMPOS_FORMULARIO = ['emailPara', 'emailCopia', 'respostaPara', 'assunto', 'texto'];

/**
 * Versão enxuta de `AcaoEnviarEmailDevedor.jsx`/`EnviarEmailDevedor.jsx` (tela
 * real de Inspecionar Processo, AW2-912) — decisão confirmada com o usuário:
 * sem editor rico (Quill), reescrita via IA (`DialogGPT`), `AutoTextoDialog`
 * ou `EmailsPadraoDialog`. Campos essenciais + anexo básico.
 */
function makeFormFields(data) {
  return {
    emailDe: data?.emailDe || '',
    emailPara: data?.emailPara || '',
    emailCopia: data?.emailCopia || '',
    respostaPara: data?.respostaPara || '',
    assunto: data?.assunto || '',
    texto: data?.texto || '',
    // submitFormEnvioEmailDevedor sempre envia esses 3 campos no FormData;
    // esta versão não tem Modelos de E-mail nem fluxo de advogado, então
    // ficam vazios sem precisar alterar o contrato do service real.
    nomeModelo: '',
    emailAdvogado: '',
    nomeAdvogado: '',
  };
}

function AcaoEnviarEmailDevedorProcesso({ idProcesso, onSucesso }) {
  const [formFields, setFormFields] = useState(() => makeFormFields());
  const [anexos, setAnexos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [errosPorCampo, setErrosPorCampo] = useState({});

  useEffect(() => {
    setCarregando(true);
    setFormFields(makeFormFields());
    setAnexos([]);
    setErrosPorCampo({});
    setErro('');
    let ignore = false;

    buscarInfoInicialLimparEnvioEmail(idProcesso)
      .then((response) => {
        if (ignore) {
          return;
        }
        setFormFields(makeFormFields(response?.data));
      })
      .catch(() => {
        if (!ignore) {
          setErro(MENSAGEM_ERRO_INICIAL);
        }
      })
      .finally(() => {
        if (!ignore) {
          setCarregando(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [idProcesso]);

  const onChangeCampo = useCallback(nome => (event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, [nome]: value }));
    setErrosPorCampo(erros => ({ ...erros, [nome]: undefined }));
  }, []);

  const onChangeAnexos = useCallback((event) => {
    setAnexos(Array.from(event.target.files || []));
  }, []);

  const onSubmit = useCallback(async (event) => {
    event.preventDefault();

    try {
      setEnviando(true);
      setErrosPorCampo({});
      setErro('');
      await submitFormEnvioEmailDevedor(idProcesso, formFields, anexos);
      onSucesso();
    } catch (exception) {
      const validations = exception.response?.data?.validations;
      const errosMapeados = (validations || []).reduce((acc, validation) => (
        CAMPOS_FORMULARIO.includes(validation.field)
          ? { ...acc, [validation.field]: validation.message }
          : acc
      ), {});

      if (Object.keys(errosMapeados).length > 0) {
        setErrosPorCampo(errosMapeados);
        setErro(MENSAGEM_REVISAR);
      } else {
        setErro(MENSAGEM_ERRO_GENERICA);
      }
    } finally {
      setEnviando(false);
    }
  }, [idProcesso, formFields, anexos, onSucesso]);

  const desabilitarEnviar = useMemo(
    () => carregando
      || enviando
      || !formFields.emailDe
      || !formFields.emailPara
      || !formFields.assunto
      || !formFields.texto,
    [carregando, enviando, formFields],
  );

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
        Envio de e-mail ao Devedor
      </Typography>

      <TextField
        fullWidth
        label="De"
        value={formFields.emailDe}
        InputProps={{ readOnly: true }}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Para*"
        value={formFields.emailPara}
        onChange={onChangeCampo('emailPara')}
        error={Boolean(errosPorCampo.emailPara)}
        helperText={errosPorCampo.emailPara}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Cópia"
        value={formFields.emailCopia}
        onChange={onChangeCampo('emailCopia')}
        error={Boolean(errosPorCampo.emailCopia)}
        helperText={errosPorCampo.emailCopia}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Resposta Para"
        value={formFields.respostaPara}
        onChange={onChangeCampo('respostaPara')}
        error={Boolean(errosPorCampo.respostaPara)}
        helperText={errosPorCampo.respostaPara}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Assunto*"
        value={formFields.assunto}
        onChange={onChangeCampo('assunto')}
        error={Boolean(errosPorCampo.assunto)}
        helperText={errosPorCampo.assunto}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        minRows={8}
        label="Texto*"
        value={formFields.texto}
        onChange={onChangeCampo('texto')}
        error={Boolean(errosPorCampo.texto)}
        helperText={errosPorCampo.texto}
        sx={{ mb: 2 }}
      />

      <Button component="label" variant="outlined" sx={{ mb: 2, display: 'block', width: 'fit-content' }}>
        Anexar arquivos
        <input type="file" multiple hidden onChange={onChangeAnexos} />
      </Button>
      {anexos.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
          {anexos.map(anexo => anexo.name).join(', ')}
        </Typography>
      )}

      <Button type="submit" variant="contained" color="success" disabled={desabilitarEnviar}>
        Enviar
      </Button>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Box>
  );
}

AcaoEnviarEmailDevedorProcesso.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSucesso: PropTypes.func.isRequired,
};

AcaoEnviarEmailDevedorProcesso.defaultProps = {
  idProcesso: null,
};

export default React.memo(AcaoEnviarEmailDevedorProcesso);
